import React, { useCallback } from 'react';
import { buildSearchLink } from 'lib/search';
import { setLocation } from 'lib/helpers';
import { format } from 'date-fns';
import { TrackingClient } from '@sparefoot/tracking-client';

function createHandleSearch(props) {
	return ({ locationParams, date, searchInputId }) => {
		let params = {
			...locationParams,
			searchType: props.searchType,
			moveInDate: date && format(date, 'MM/dd/yyyy'),
		};

		if (props.searchParams) {
			params.order = props.searchParams.order;
			params.vehicle = props.searchParams.vehicle;
			params.searchType = props.searchParams.searchType;
			if (props.searchParams.amenity) {
				// allow for searching between amenity landing pages
				// api needs to see the amenity key to send back an
				// amenity canonical url to do a redirect
				params.amenity = props.searchParams.amenity;
			}
		}

		if (props.additionalSearchParams) {
			params = { ...params, ...props.additionalSearchParams };
		}

		TrackingClient.trackSearchSubmitted({
			search_filters: params.vehicle || 'Self Storage', // filters for the search field
			search_label: params.locationSource, // location on page
			search_term: searchInputId,
		});

		setLocation(buildSearchLink(params, props.searchPath));
	};
}

/**
 * Hook version of withSearchHandler
 */
export function useSearchHandler({
	searchType,
	searchParams,
	additionalSearchParams,
	searchPath,
}) {
	const handleSearch = useCallback(
		({ locationParams, date, searchInputId }) => {
			let params = {
				...locationParams,
				searchType,
				moveInDate: date && format(date, 'MM/dd/yyyy'),
			};

			if (searchParams) {
				params.order = searchParams.order;
				params.vehicle = searchParams.vehicle;
				params.searchType = searchParams.searchType;
				if (searchParams.amenity) {
					params.amenity = searchParams.amenity;
				}
			}

			if (additionalSearchParams) {
				params = { ...params, ...additionalSearchParams };
			}

			TrackingClient.trackSearchSubmitted({
				search_filters: params.vehicle || 'Self Storage',
				search_label: params.locationSource,
				search_term: searchInputId,
			});

			setLocation(buildSearchLink(params, searchPath));
		},
		[searchType, searchParams, additionalSearchParams, searchPath],
	);

	return { handleSearch };
}

/**
 * Basic Search Handler HOC for search box that generates location type and date
 */
const withSearchHandler = (WrappedComponent) => {
	const WithSearchHandler = (props) => {
		const handleSearch = createHandleSearch(props);

		return (
			<WrappedComponent
				{...props}
				handleSearch={handleSearch}
			/>
		);
	};

	return WithSearchHandler;
};

export default withSearchHandler;
